import { useState, useEffect } from 'react';
import { RouteComponentProps } from '@reach/router';
import { useMutation } from '@apollo/client';
import { useAuth } from '@calefy-inc/authentication';
import { Form, Calendar } from '@calefy-inc/informedMaterial';
import Stack from '@mui/material/Stack';
import {
  withFormStyles,
  WithFormStyles,
} from '../../../../util/withFormStyles';
import { useAllowByPermission, useUsingVagoSettings } from '../../../../hooks';
import { useIsPolicyStreamOrganization } from '../../../PolicyStream/hooks/useIsPolicyStreamOrganization';
import Page404 from '../Page404';
import { InsufficientPermissionsErrorPage } from '../../../common/ErrorHandling';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import TaskIcon from '@mui/icons-material/Task';
import Collapse from '@mui/material/Collapse';
import { graphql } from '../../../../gql';
import { ErrorP } from '../../../common/Typography';
import { downloadZipFile } from '@calefy-inc/utility';
import {
  CoreTaskStatusChoices,
  HandleVagoBordereauMutation,
} from '../../../../gql/graphql';
import { useLazyQuery } from '@apollo/client';
import { GET_TASK_POOL } from '../../../../queries';
import Bugsnag from '@bugsnag/browser';
import { GetTaskPoolQuery } from '../../../../gql/graphql';
import { errorify } from '@calefy-inc/utility';
import { isNotNullish } from '../../../../util';
import { ArrayElement } from '@calefy-inc/utilityTypes';
import { SupportLink } from '../../../common/SupportLink';

interface ManagementPanelBordereauProps
  extends RouteComponentProps,
    WithFormStyles {}
export const ManagementPanelBordereau = withFormStyles()(
  ({ classes }: ManagementPanelBordereauProps) => {
    const [taskInProgress, setTaskInProgress] = useState<boolean>(false);
    const [handleVagoBordereau, { loading, error, data }] = useMutation(
      HANDLE_VAGO_BORDEREAU,
    );
    const { token } = useAuth();
    const onVago = useUsingVagoSettings();
    const hasBordereauPermissions = useAllowByPermission('send:bordereau');
    const isPolicystreamOrganization = useIsPolicyStreamOrganization();
    const [sendEmail, setSendEmail] = useState<boolean>(false);

    if (hasBordereauPermissions === false || isPolicystreamOrganization) {
      return <InsufficientPermissionsErrorPage />;
    }
    if (onVago === false) {
      return <Page404 />;
    }
    if (hasBordereauPermissions === undefined || onVago === undefined) {
      return null;
    }
    return (
      <>
        <h1>Bordereau</h1>

        <Typography>
          Please select the start and end date for the bordereau documents. In
          addition, please select whether or not a new copy of the bordereau
          email should be sent.
        </Typography>
        <Form
          initialValues={convertPreviousMonthDatesToFormFormat(
            getPreviousMonthDates(),
          )}
          onSubmit={async (formState) => {
            const { values } = formState;
            const startDate = String(values.startDate);
            const endDate = String(values.endDate);
            const correctStartDate = convertFormTimeToCorrect(startDate);
            const correctEndDate = convertFormTimeToCorrect(endDate);
            handleVagoBordereau({
              variables: {
                token,
                startDate: correctStartDate,
                endDate: correctEndDate,
                sendEmail,
              },
            });
          }}
        >
          <Stack spacing={4} sx={{ marginTop: 2 }}>
            <Calendar
              name='startDate'
              label='Start Date'
              required={true}
              classes={classes.calendar}
              disabled={taskInProgress}
            />
            <Calendar
              name='endDate'
              label='End Date'
              required={true}
              classes={classes.calendar}
              disabled={taskInProgress}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendEmail}
                  onChange={(e) => {
                    setSendEmail(e.target.checked);
                  }}
                  disabled={taskInProgress}
                />
              }
              label='Send Email?'
            />
            <DisplayBordereauTasks
              taskPool={data?.handleVagoBordereau?.taskPool}
              setTaskInProgress={(inProgress: boolean) =>
                setTaskInProgress(inProgress)
              }
            />
            <SubmitButtonContainer>
              <LoadingButton
                type='submit'
                loading={loading}
                variant='contained'
                startIcon={<DownloadRoundedIcon />}
                disabled={taskInProgress}
              >
                Download Bordereau{sendEmail ? ' and Send Email' : ''}
              </LoadingButton>
            </SubmitButtonContainer>
            <Collapse in={!!error}>
              <Typography>Error Generating Bordereau</Typography>
              <Typography>
                Unfortunately there was an error generating the bordereau.
                Please refresh the page and try again. If the error persists,
                contact our support team at{' '}
                <SupportLink
                  subject='Error initiating bordereau generation'
                  body={error?.message}
                />
                .
              </Typography>
            </Collapse>
          </Stack>
        </Form>
      </>
    );
  },
);

const POLLING_INTERVAL_MS = 500;
type BackendTask = ArrayElement<
  NonNullable<GetTaskPoolQuery['getTaskPool']>['tasks']
>;
interface DisplayBordereauTaskProps {
  taskPool: NonNullable<
    HandleVagoBordereauMutation['handleVagoBordereau']
  >['taskPool'];
  setTaskInProgress: (inProgress: boolean) => void;
}
const DisplayBordereauTasks = ({
  taskPool,
  setTaskInProgress,
}: DisplayBordereauTaskProps) => {
  const [downloadSuccess, setDownloadSuccess] = useState<boolean>(false);
  const [downloadError, setDownloadError] = useState<Error | undefined>();
  const [tasks, setTasks] = useState<Array<BackendTask>>([]);
  const [additionalInformation, setAdditionalInformation] = useState<
    object | undefined
  >();
  // @ts-expect-error
  const [getTaskPool, { error, data, stopPolling, startPolling, reset }] =
    useLazyQuery(GET_TASK_POOL);

  // once the data has been loaded, start polling for the task pool
  useEffect(() => {
    if (taskPool) {
      getTaskPool({
        variables: { taskPoolId: taskPool.id },
      });
      startPolling?.(POLLING_INTERVAL_MS);
      setTaskInProgress(true);
    } else {
      setTaskInProgress(false);
      stopPolling?.();
    }
    setTasks([]);
    setDownloadError(undefined);
    setDownloadSuccess(false);
    reset?.();
    setAdditionalInformation(undefined);
  }, [taskPool]);

  // set the tasks and additional information
  useEffect(() => {
    if (data?.getTaskPool) {
      setTasks(
        [...data.getTaskPool.tasks]
          .filter(isNotNullish)
          // @ts-expect-error
          .sort((a, b) => a.order - b.order),
      );
      try {
        const { additionalInformation } = data.getTaskPool;
        if (!additionalInformation) {
          setAdditionalInformation(undefined);
        } else {
          const parsed = JSON.parse(additionalInformation);
          setAdditionalInformation(parsed);
        }
      } catch (e) {
        console.error(e);
        setAdditionalInformation(undefined);
      }
    } else {
      setTasks([]);
      setAdditionalInformation(undefined);
    }
  }, [data]);

  // stop polling if there's an error or if all of the tasks are complete
  useEffect(() => {
    if (!stopPolling) {
      return;
    }
    if (error) {
      stopPolling();
    }
    if (data?.getTaskPool?.error) {
      stopPolling();
    }
    if (
      tasks.length > 0 &&
      tasks.every((task) => task.status === CoreTaskStatusChoices.Completed) &&
      additionalInformation &&
      Object.keys(additionalInformation).length > 0
    ) {
      setTaskInProgress(false);
      stopPolling();
    }
  }, [error, data, tasks, additionalInformation]);

  // download the file if present
  useEffect(() => {
    if (
      tasks.length > 0 &&
      tasks.every((task) => task.status === CoreTaskStatusChoices.Completed) &&
      data?.getTaskPool?.additionalInformation
    ) {
      (async () => {
        try {
          const additionalInformation = JSON.parse(
            data?.getTaskPool?.additionalInformation,
          );
          const { bordereau_files: files } = additionalInformation;
          if (!files) {
            return;
          }
          for (let file of files) {
            const { as_b64: zipData, filename: zipFilename } = file;
            await downloadZipFile(zipData, zipFilename);
          }
          setDownloadSuccess(true);
        } catch (e) {
          console.error(`Error parsing additional information:`, e);
          Bugsnag.notify(errorify(e));
          setDownloadSuccess(false);
          setDownloadError(errorify(e));
        }
      })();
    }
  }, [data, tasks]);

  if (!taskPool) {
    return null;
  }

  if (error || data?.getTaskPool?.error) {
    return <DisplayTaskPoolError error={error || data?.getTaskPool?.error} />;
  }
  return (
    <TaskPoolContainer>
      <Collapse in={tasks.length > 0}>
        <Typography variant='h4'>Bordereau Generation</Typography>
        <List>
          {tasks.map((task) => (
            <ListItem key={task.id}>
              <ListItemIcon>
                {task.status === CoreTaskStatusChoices.Completed ? (
                  <CheckIcon sx={{ color: 'success.main' }} />
                ) : task.status === CoreTaskStatusChoices.Pending ? (
                  <RotatingAutorenewIcon />
                ) : task.status === CoreTaskStatusChoices.Failed ? (
                  <CloseIcon sx={{ color: 'error.main' }} />
                ) : (
                  <TaskIcon />
                )}
              </ListItemIcon>
              {task.description}
              {task.numSubtasks &&
              task.status !== CoreTaskStatusChoices.Completed
                ? ` - ${task.numCompletedSubtasks} / ${
                    task.numSubtasks
                  } Application${task.numSubtasks === 1 ? '' : 's'}`
                : ''}
            </ListItem>
          ))}
          <ListItem>
            <ListItemIcon>
              {downloadSuccess ? (
                <CheckIcon sx={{ color: 'success.main' }} />
              ) : downloadError ? (
                <CloseIcon sx={{ color: 'error.main' }} />
              ) : (
                <RotatingAutorenewIcon />
              )}
            </ListItemIcon>
            Download
          </ListItem>
        </List>
      </Collapse>
      <Collapse in={!!downloadError}>
        <DownloadErrorContainer>
          <Typography>Error Downloading Bordereau</Typography>
          <Typography>
            Unforunately, there was an error downloading the bordereau. Please
            refresh the page and try again. If the error persists, contact our
            support team at{' '}
            <SupportLink
              subject='Error downloading bordereau file'
              body={downloadError?.message}
            />
            .
          </Typography>
        </DownloadErrorContainer>
      </Collapse>
    </TaskPoolContainer>
  );
};

const RotatingAutorenewIcon = styled(AutorenewIcon)(
  `
  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  animation: rotate 2s linear infinite;
`,
);

const DownloadErrorContainer = styled('div')({});

interface DisplayTaskPoolErrorProps {
  error?: string | Error | null;
}
const DisplayTaskPoolError = ({ error }: DisplayTaskPoolErrorProps) => {
  if (!error) {
    return null;
  }
  return (
    <ErrorContainer>
      <Typography>
        We're sorry, but an error has occurred with generating the bordereau.
      </Typography>
      <ErrorP>
        Error: {typeof error === 'string' ? error : error.message}
      </ErrorP>
      <Typography>
        Please try refreshing the page and trying again. If this error persists,
        please contact our support team at{' '}
        <SupportLink
          subject={`Error generating bordereau`}
          body={typeof error === 'string' ? error : error.message}
        />
        .
      </Typography>
    </ErrorContainer>
  );
};
const ErrorContainer = styled('div')({});

const TaskPoolContainer = styled('div')(({}) => {
  return {};
});

const SubmitButtonContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
});

const convertFormTimeToCorrect = (rawDate: string | Date) => {
  if (typeof rawDate === 'string') {
    const [month, day, year] = rawDate.split('/');
    return [year, month, day].join('-');
  }
  const year = rawDate.getFullYear();
  const month = String(rawDate.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
  const day = String(rawDate.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const getPreviousMonthDates = () => {
  const now = new Date();

  const year = now.getFullYear();
  const prevMonth = now.getMonth() - 1;
  // Get the first day of the previous month
  const startDate = new Date(year, prevMonth, 1);

  // Get the last day of the previous month
  const endDate = new Date(year, prevMonth + 1, 0);

  const dates = { startDate, endDate };
  return dates;
};

const convertDateToMMDDYYYY = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

const convertPreviousMonthDatesToFormFormat = (dates: {
  startDate: Date;
  endDate: Date;
}): { startDate: string; endDate: string } => {
  return {
    startDate: convertDateToMMDDYYYY(dates.startDate),
    endDate: convertDateToMMDDYYYY(dates.endDate),
  };
};

const HANDLE_VAGO_BORDEREAU = graphql(`
  mutation handleVagoBordereau(
    $token: String!
    $startDate: Date!
    $endDate: Date!
    $sendEmail: Boolean!
  ) {
    handleVagoBordereau(
      token: $token
      startDate: $startDate
      endDate: $endDate
      sendEmail: $sendEmail
    ) {
      taskPool {
        id
      }
    }
  }
`);
